import i18n from "@/i18n";
/**
 * VVCard — TES-B2C1B1 §15-§34. Renders the canonical backend VV
 * state (GET /workforce/employees/{id}/vv). No VV math in React:
 * every count/threshold comes from the server; hours are display-only.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import VVOpeningStateDialog from "./VVOpeningStateDialog";
import { browserLocalYMD, formatDurationMinutes } from "./workforceHelpers";

const PROFILE_COPY = {
    get REGULAR_NIGHT_VV() { return [i18n.t("wf.vVCard.regularNightVv", "Regular night-work VV"),
        i18n.t("wf.vVCard.notYetCalculated", "This VV system is not yet calculated by MiseOne.")]; },
    get OFFICE_PROTOCOL() { return [i18n.t("wf.vVCard.officeProtocol", "Office work-time protocol"),
        i18n.t("wf.vVCard.standardNotApplied", "Standard VV calculation is not applied.")]; },
    get PERSONNEL_RESTAURANT_LOCAL_ALTERNATIVE() { return [i18n.t("wf.vVCard.localArrangement", "Local VV arrangement"),
        i18n.t("wf.vVCard.standardNotApplied", "Standard VV calculation is not applied.")]; },
};
const OPENING_EXPLAIN = "MiseOne needs the employee's starting VV "
    + "position before it can calculate the current balance.";

function Row({ label, value, testId, attention }) {
    return (
        <div className="flex items-center justify-between py-1.5 text-sm">
            <span className="text-neutral-500">{label}</span>
            <span data-testid={testId}
                className={`tabular-nums ${attention
                    ? "font-medium text-amber-700" : "text-neutral-900"}`}>
                {value}
            </span>
        </div>
    );
}

function Notice({ testId, title, body, action }) {
    return (
        <div data-testid={testId} className="space-y-2">
            <p className="text-sm font-medium text-neutral-900">{title}</p>
            {body && <p className="text-sm text-neutral-500">{body}</p>}
            {action && <div className="pt-1">{action}</div>}
        </div>
    );
}

function Unallocated({ count }) {
    if (!count) return null;
    return (
        <p data-testid="vv-unallocated" role="status"
            className="mt-3 rounded-md border border-amber-200 bg-amber-50
                        px-3 py-2 text-xs text-amber-900">
            {i18n.t("wf.vVCard.unallocated", { count, defaultValue_one: "1 VV use recorded without an available entitlement.", defaultValue_other: "{{count}} VV uses recorded without an available entitlement." })}
        </p>
    );
}

export function NextProgress({ state }) {
    if (state.next_threshold_minutes == null) {
        return (
            <p data-testid="vv-annual-max"
                className="text-sm text-neutral-700">
                {i18n.t("wf.vVCard.annualVvMaximumReached", "Annual VV maximum reached")}
            </p>
        );
    }
    const done = state.eligible_minutes_ytd || 0;
    const target = state.next_threshold_minutes;
    const pct = target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0;
    return (
        <div data-testid="vv-next-progress">
            <div className="flex items-baseline justify-between text-sm">
                <span className="text-neutral-500">{i18n.t("wf.vVCard.nextVv", "Next VV")}</span>
                <span className="tabular-nums text-neutral-900"
                    data-testid="vv-next-progress-value">
                    {formatDurationMinutes(done)} / {formatDurationMinutes(target)}
                </span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full
                            bg-neutral-100">
                <div className="h-full rounded-full bg-neutral-900"
                    style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1.5 text-xs text-neutral-500"
                data-testid="vv-next-remaining">
                {formatDurationMinutes(state.minutes_to_next_threshold)} to go
            </p>
        </div>
    );
}

function Balance({ s, testPrefix = "vv" }) {
    return (
        <div className="divide-y divide-neutral-100">
            <Row label={i18n.t("wf.vVCard.earnedThisYear", "Earned this year")} value={s.earned_count_ytd}
                testId={`${testPrefix}-earned`} />
            <Row label={i18n.t("wf.vVCard.used", "Used")} value={s.used_count_ytd}
                testId={`${testPrefix}-used`} />
            <Row label={i18n.t("wf.vVCard.overdue", "Overdue")}
                value={s.overdue_count > 0
                    ? `${s.overdue_count} overdue VV` : 0}
                attention={s.overdue_count > 0}
                testId={`${testPrefix}-overdue`} />
        </div>
    );
}

export function Calculable({ state }) {
    return (
        <div data-testid="vv-state-calculable">
            <div className="mb-3">
                <div className="text-4xl font-semibold tracking-tight
                                tabular-nums text-neutral-900"
                    data-testid="vv-available">
                    {state.available_count}
                </div>
                <div className="text-xs text-neutral-500">{i18n.t("wf.vVCard.available", "Available")}</div>
            </div>
            <Balance s={state} />
            <div className="mt-4"><NextProgress state={state} /></div>
            <Unallocated count={state.unallocated_vv_use_count} />
        </div>
    );
}

function Reopened({ state }) {
    const lf = state.last_finalized;
    return (
        <div data-testid="vv-state-reopened" className="space-y-3">
            <Notice title={i18n.t("wf.vVCard.vvPendingReclose", "VV pending reclose")}
                body={i18n.t("wf.vVCard.theCurrentCalculatedBalanceIsNot", "The current calculated balance is not final.")} />
            {lf && (
                <div data-testid="vv-last-finalized"
                    className="rounded-md bg-neutral-50 px-3 py-2">
                    <div className="text-xs font-medium text-neutral-500">
                        {i18n.t("wf.vVCard.lastFinalizedBalance", "Last finalized balance")}
                    </div>
                    <Row label={i18n.t("wf.vVCard.available", "Available")} value={lf.available_count}
                        testId="vv-lf-available" />
                    <Balance s={lf} testPrefix="vv-lf" />
                    <Unallocated count={lf.unallocated_vv_use_count} />
                </div>
            )}
        </div>
    );
}

export default function VVCard({
    employeeId, canManage, canManageOpening, hasTerms, reloadKey, onSetVvSystem,
}) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [openingOpen, setOpeningOpen] = useState(false);

    const load = useCallback(async () => {
        if (!employeeId || !hasTerms) return;
        setLoading(true);
        setError(false);
        try {
            const { data } = await api.get(
                `/workforce/employees/${employeeId}/vv`,
                { params: { as_of: browserLocalYMD() } });
            setState(data && data.status ? data : null);
            if (!data || !data.status) setError(true);
        } catch (_err) {
            setState(null);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [employeeId, hasTerms]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const setSystemBtn = canManage && (
        <Button variant="outline" size="sm" onClick={onSetVvSystem}
            data-testid="vv-set-system-btn">{i18n.t("wf.vVCard.setVvSystem", "Set VV system")}</Button>
    );
    const openingBtn = (label, testId) => canManageOpening && (
        <Button variant="outline" size="sm" data-testid={testId}
            onClick={() => setOpeningOpen(true)}>{label}</Button>
    );

    let body;
    if (!hasTerms) {
        body = <Notice testId="vv-state-no-terms"
            title={i18n.t("wf.vVCard.noEmploymentTerms", "No Employment Terms")}
            body={i18n.t("wf.vVCard.vvIsShownOnceTheEmployee", "VV is shown once the employee has Employment Terms.")} />;
    } else if (loading) {
        body = (
            <div data-testid="vv-card-loading" className="space-y-2">
                <Skeleton className="h-9 w-16" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
            </div>
        );
    } else if (error || !state) {
        body = <Notice testId="vv-card-error" title={i18n.t("wf.vVCard.vvStateUnavailable", "VV state unavailable")}
            body={i18n.t("wf.vVCard.theVvBalanceCouldNotBe", "The VV balance could not be loaded.")}
            action={<Button variant="outline" size="sm" onClick={load}
                data-testid="vv-card-retry">{i18n.t("wf.vVCard.retry", "Retry")}</Button>} />;
    } else {
        const vs = state.vv_profile?.vv_system;
        switch (state.status) {
        case "CALCULABLE":
            body = <Calculable state={state} />;
            break;
        case "REOPENED_PENDING_RECLOSE":
            body = <Reopened state={state} />;
            break;
        case "VV_PROFILE_UNRESOLVED":
            body = <Notice testId="vv-state-unresolved"
                title={i18n.t("wf.vVCard.vvSystemNeedsSetup", "VV system needs setup")}
                body={i18n.t("wf.vVCard.chooseTheVvSystemInEmployment", "Choose the VV system in Employment Terms to see the balance.")}
                action={setSystemBtn} />;
            break;
        case "OPENING_STATE_REQUIRED":
            body = <Notice testId="vv-state-opening-required"
                title={i18n.t("wf.vVCard.openingVvStateRequired", "Opening VV state required")} body={i18n.t("wf.VVCard.OPENING_EXPLAIN", OPENING_EXPLAIN)}
                action={openingBtn("Set opening VV state",
                    "vv-set-opening-btn")} />;
            break;
        case "HISTORICAL_CLOSE_SOURCE_INSUFFICIENT":
            body = <Notice testId="vv-state-historical"
                title={i18n.t("wf.vVCard.historicalVvStartingStateRequired", "Historical VV starting state required")}
                body={i18n.t("wf.VVCard.OPENING_EXPLAIN", OPENING_EXPLAIN)}
                action={openingBtn("Set opening VV state",
                    "vv-set-opening-btn")} />;
            break;
        case "VV_NOT_APPLICABLE":
            body = <Notice testId="vv-state-not-applicable"
                title={i18n.t("wf.vVCard.standardVvNotApplicable", "Standard VV not applicable")}
                body={i18n.t("wf.vVCard.threeShiftWorkStandardVvDoes", "Three-shift work. Standard VV does not apply.")} />;
            break;
        default: {
            const [title, text] = PROFILE_COPY[vs] || [i18n.t("wf.vVCard.standardVv", "Standard VV"),
                i18n.t("wf.vVCard.notYetCalculated", "This VV system is not yet calculated by MiseOne.")];
            body = <Notice testId="vv-state-unsupported"
                title={title} body={text} />;
        }
        }
    }

    const isRevision = state?.status === "CALCULABLE";
    return (
        <section data-testid="vv-card">
            <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-700">
                    {i18n.t("wf.vVCard.vvVuosivapaa", "VV / Vuosivapaa")}
                </h3>
                {isRevision && openingBtn("Update opening state",
                    "vv-update-opening-btn")}
            </div>
            <div className="rounded-md border border-neutral-200 bg-white p-4">
                {body}
            </div>
            <VVOpeningStateDialog open={openingOpen}
                onOpenChange={setOpeningOpen} employeeId={employeeId}
                isRevision={isRevision} onSaved={load} />
        </section>
    );
}
